import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { X, Plus, Trash2, Loader2, GitBranch, Palette, Type, Undo, Redo, Calendar, Sparkles } from 'lucide-react';

const pillStyle = { borderRadius: '9999px' };

// --- Custom Mind Map Node ---
const MindMapNode = ({ id, data, selected, onLabelChange }) => {
  const isRoot = data.isRoot;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data.label || '');

  useEffect(() => {
    setEditValue(data.label || '');
  }, [data.label]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue.trim() !== data.label) {
      if (onLabelChange) {
        onLabelChange(id, editValue.trim());
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(data.label || '');
    }
  };

  return (
    <div
      className={`transition-shadow ${
        selected ? 'shadow-md ring-2 ring-blue-400/40' : 'shadow-sm hover:shadow-md'
      }`}
      style={{
        backgroundColor: isRoot ? '#292524' : (data.color || '#f5f5f4'),
        borderRadius: '8px',
        minWidth: isRoot ? '120px' : '100px',
        maxWidth: '200px',
        padding: isRoot ? '12px 20px' : '8px 16px',
        border: isRoot ? 'none' : (data.color ? 'none' : '1px solid #e7e5e4'),
      }}
    >
      {isEditing ? (
        <input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full text-xs text-center border-none bg-transparent outline-none font-semibold leading-snug"
          style={{
            color: (isRoot || data.color) ? '#fff' : '#292524',
          }}
        />
      ) : (
        <div
          onDoubleClick={handleDoubleClick}
          className="font-semibold leading-snug break-words cursor-text"
          style={{
            fontSize: isRoot ? '14px' : '12px',
            color: (isRoot || data.color) ? '#fff' : '#292524',
            textAlign: 'center',
          }}
        >
          {data.label || 'Untitled'}
        </div>
      )}
      {data.description && (
        <div
          className="mt-1 leading-relaxed"
          style={{
            fontSize: '10px',
            color: (isRoot || data.color) ? 'rgba(255,255,255,0.65)' : '#a8a29e',
            textAlign: 'center',
          }}
        >
          {data.description}
        </div>
      )}
      <Handle id="target-top" type="target" position={Position.Top} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="target-left" type="target" position={Position.Left} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="target-right" type="target" position={Position.Right} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={{ opacity: 0, width: 16, height: 16 }} />

      <Handle id="source-top" type="source" position={Position.Top} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="source-left" type="source" position={Position.Left} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="source-right" type="source" position={Position.Right} style={{ opacity: 0, width: 16, height: 16 }} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={{ opacity: 0, width: 16, height: 16 }} />
    </div>
  );
};

// --- Scattered radial auto-layout ---
// --- Organic radial layout ---
// --- Symmetrical horizontal split mind map layout ---
const radialLayout = (rootId, allNodes, allEdges) => {
  const children = {};
  allEdges.forEach(e => {
    if (!children[e.source]) children[e.source] = [];
    children[e.source].push(e.target);
  });

  const positions = {};
  positions[rootId] = { x: 350, y: 200 };

  const directKids = children[rootId] || [];
  
  // Divide children between left and right sides
  const rightKids = directKids.slice(0, Math.ceil(directKids.length / 2));
  const leftKids = directKids.slice(Math.ceil(directKids.length / 2));

  // Spacing configs
  const hSpace = 240;
  const vSpace = 100;

  const layoutBranch = (kids, dir) => {
    const total = kids.length;
    if (total === 0) return;

    // Center the branch vertically relative to the root node (y = 200)
    const startY = 200 - ((total - 1) * vSpace) / 2;

    kids.forEach((kidId, idx) => {
      const kidX = 350 + dir * hSpace;
      const kidY = startY + idx * vSpace;
      positions[kidId] = { x: kidX, y: kidY };

      // Recursively layout grandchildren propagating outwards
      const traverseSub = (parentId, parentX, parentY) => {
        const subKids = children[parentId] || [];
        const subTotal = subKids.length;
        if (subTotal === 0) return;

        const subStartY = parentY - ((subTotal - 1) * vSpace) / 2;
        subKids.forEach((subId, subIdx) => {
          const subX = parentX + dir * hSpace;
          const subY = subStartY + subIdx * vSpace;
          positions[subId] = { x: subX, y: subY };
          traverseSub(subId, subX, subY);
        });
      };

      traverseSub(kidId, kidX, kidY);
    });
  };

  layoutBranch(rightKids, 1);
  layoutBranch(leftKids, -1);

  return positions;
};

// --- Determine best edge handle IDs ---
const getEdgeHandles = (sourceNode, targetNode) => {
  if (!sourceNode || !targetNode) return { sourceHandle: 'source-bottom', targetHandle: 'target-top' };
  const dx = targetNode.position.x - sourceNode.position.x;
  const dy = targetNode.position.y - sourceNode.position.y;
  
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 
      ? { sourceHandle: 'source-right', targetHandle: 'target-left' } 
      : { sourceHandle: 'source-left', targetHandle: 'target-right' };
  } else {
    return dy > 0 
      ? { sourceHandle: 'source-bottom', targetHandle: 'target-top' } 
      : { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
  }
};

// --- Colors ---
const NODE_COLORS = [
  null,
  '#292524',
  '#78716c',
  '#1e3a5f',
  '#2d4a3e',
  '#6b3fa0',
  '#a16207',
  '#9f1239',
];

// --- Main Editor ---
const MAX_HISTORY = 50;

const MindMapEditor = ({
  isDarkMode,
  theme,
  onSave,
  onClose,
  initialMap = null,
  saveStatus
}) => {
  const [title, setTitle] = useState(initialMap?.title || '');
  const [description, setDescription] = useState(initialMap?.description || '');
  const [status, setStatus] = useState(initialMap?.status || 'draft');
  const [category, setCategory] = useState(initialMap?.category || '');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [date, setDate] = useState(() => {
    if (initialMap?.date) return initialMap.date;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [showColorPicker, setShowColorPicker] = useState(null);
  const rfInstance = useRef(null);
  // Center view on mount using defaultViewport
  const initialViewport = { x: 350, y: 200, zoom: 1.5 };

  const parseNodes = () => {
    if (initialMap?.nodes?.length > 0) {
      return initialMap.nodes.map((n, i) => ({
        ...n,
        type: 'mindMap',
        data: { ...n.data, isRoot: i === 0 },
      }));
    }
    return [
      {
        id: 'root',
        type: 'mindMap',
        position: { x: 350, y: 200 },
        data: { label: 'Main Idea', description: '', isRoot: true },
      },
    ];
  };

  const initialNodes = useRef(null);
  const initialEdges = useRef(null);
  if (initialNodes.current === null) {
    initialNodes.current = parseNodes();
    initialEdges.current = (initialMap?.edges || []).map(e => ({
      ...e,
      sourceHandle: undefined,
      targetHandle: undefined,
      type: 'simplebezier',
    }));
  }

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes.current);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges.current);

  // Undo/Redo history stack using refs
  const historyRef = useRef([{
    nodes: JSON.parse(JSON.stringify(initialNodes.current)),
    edges: JSON.parse(JSON.stringify(initialEdges.current)),
  }]);
  const historyIndexRef = useRef(0);
  const [, forceUpdate] = useState(0);

  const recordState = useCallback((newNodes, newEdges) => {
    const nextNodes = JSON.parse(JSON.stringify(newNodes));
    const nextEdges = JSON.parse(JSON.stringify(newEdges));
    const h = historyRef.current.slice(0, historyIndexRef.current + 1);
    h.push({ nodes: nextNodes, edges: nextEdges });
    if (h.length > MAX_HISTORY) h.shift();
    historyRef.current = h;
    historyIndexRef.current = h.length - 1;
    forceUpdate(n => n + 1);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    const nextIdx = historyIndexRef.current - 1;
    const snap = historyRef.current[nextIdx];
    setNodes(snap.nodes);
    setEdges(snap.edges);
    historyIndexRef.current = nextIdx;
    forceUpdate(n => n + 1);
  }, [setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIdx = historyIndexRef.current + 1;
    const snap = historyRef.current[nextIdx];
    setNodes(snap.nodes);
    setEdges(snap.edges);
    historyIndexRef.current = nextIdx;
    forceUpdate(n => n + 1);
  }, [setNodes, setEdges]);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const updateWorkspaceState = useCallback((nodeUpdater, edgeUpdater) => {
    setNodes((nds) => {
      const nextNodes = typeof nodeUpdater === 'function' ? nodeUpdater(nds) : nodeUpdater;
      setEdges((eds) => {
        const nextEdges = typeof edgeUpdater === 'function' ? edgeUpdater(eds) : (edgeUpdater || eds);
        recordState(nextNodes, nextEdges);
        return nextEdges;
      });
      return nextNodes;
    });
  }, [setNodes, setEdges, recordState]);

  const nodeTypes = useMemo(() => ({
    mindMap: (props) => (
      <MindMapNode
        {...props}
        onLabelChange={(nodeId, nextLabel) => {
          updateWorkspaceState((nds) => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, label: nextLabel } } : n), null);
        }}
      />
    )
  }), [updateWorkspaceState]);

  const onConnect = useCallback(
    (params) => {
      const { sourceHandle, targetHandle, ...rest } = params;
      updateWorkspaceState(null, (eds) => addEdge({ ...rest, type: 'simplebezier', style: { stroke: '#a8a29e', strokeWidth: 1.5 } }, eds));
    },
    [updateWorkspaceState]
  );

  const addChildNode = useCallback(() => {
    const selected = nodes.find(n => n.selected);
    const parentId = selected?.id || 'root';
    const parentNode = nodes.find(n => n.id === parentId);

    const childCount = edges.filter(e => e.source === parentId).length;
    const arcStart = -120;
    const arcEnd = 120;
    const arcRange = arcEnd - arcStart;
    const angleDeg = arcStart + (childCount / Math.max(childCount + 1, 2)) * arcRange + (Math.random() - 0.5) * 20;
    const dist = 160 + Math.random() * 50;

    const basePos = parentNode
      ? {
          x: parentNode.position.x + Math.cos(angleDeg * Math.PI / 180) * dist,
          y: parentNode.position.y + Math.sin(angleDeg * Math.PI / 180) * dist,
        }
      : { x: 200, y: 0 };

    const newId = `node_${Date.now()}`;
    const nextNode = { id: newId, type: 'mindMap', position: basePos, data: { label: 'New Idea', description: '' } };
    const nextEdge = {
      id: `edge_${parentId}_${newId}`,
      source: parentId,
      target: newId,
      type: 'simplebezier',
      style: { stroke: '#a8a29e', strokeWidth: 1.5 },
    };

    updateWorkspaceState(
      (nds) => nds.concat(nextNode),
      (eds) => eds.concat(nextEdge)
    );
  }, [nodes, edges, updateWorkspaceState]);

  const deleteSelected = useCallback(() => {
    const selectedIds = new Set(nodes.filter(n => n.selected).map(n => n.id));
    if (selectedIds.size === 0) return;
    if (selectedIds.has('root')) { selectedIds.delete('root'); if (selectedIds.size === 0) return; }
    
    updateWorkspaceState(
      (nds) => nds.filter(n => !selectedIds.has(n.id)),
      (eds) => eds.filter(e => !selectedIds.has(e.source) && !selectedIds.has(e.target))
    );
  }, [nodes, updateWorkspaceState]);

  const handleRadialLayout = useCallback(() => {
    const rootNode = nodes.find(n => n.id === 'root') || nodes[0];
    if (!rootNode) return;
    const newPositions = radialLayout(rootNode.id, nodes, edges);
    // Keep root node at its current position and adjust other positions relative to it
    const dx = rootNode.position.x - (newPositions[rootNode.id]?.x || 0);
    const dy = rootNode.position.y - (newPositions[rootNode.id]?.y || 0);
    updateWorkspaceState(
      (nds) => nds.map(n => {
        const pos = newPositions[n.id];
        return pos ? { ...n, position: { x: pos.x + dx, y: pos.y + dy } } : n;
      }),
      null
    );
    // Smoothly animate the viewport to fit the new layout
    setTimeout(() => {
      if (rfInstance.current) {
        rfInstance.current.fitView({ padding: 0.2, duration: 600 });
      }
    }, 50);
  }, [nodes, edges, updateWorkspaceState]);

  const colorSelected = useCallback((color) => {
    updateWorkspaceState(
      (nds) => nds.map(n => n.selected ? { ...n, data: { ...n.data, color } } : n),
      null
    );
    setShowColorPicker(null);
  }, [updateWorkspaceState]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') { e.preventDefault(); deleteSelected(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); handleRedo(); }
      if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); addChildNode(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [deleteSelected, addChildNode, handleUndo, handleRedo]);

  const handleAiSuggestCategory = async () => {
    const apiKey = localStorage.getItem('gemini_api_key');
    if (!apiKey) {
      alert('Sila tetapkan Gemini API Key dalam panel konfigurasi Admin terlebih dahulu.');
      return;
    }
    
    setIsAiLoading(true);
    try {
      const nodeLabels = nodes.map(n => n.data?.label || '').filter(Boolean).join(', ');
      const prompt = `Suggest the most suitable single-word or short-phrase category in Malay for a mind map with:
Title: "${title}"
Description: "${description}"
Nodes labels: "${nodeLabels}"

Respond ONLY with the name of the category (one word or short phrase, do not include punctuation, explanations, or quotes).`;

      const generateWithModel = async (apiVersion, modelName) => {
        const url = `https://generativelanguage.googleapis.com/${apiVersion}/models/${modelName}:generateContent?key=${apiKey}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }]
          })
        });
        return { status: response.status, data: await response.json() };
      };

      // Try stable v1 first
      let res = await generateWithModel('v1', 'gemini-1.5-flash');
      let data = res.data;

      // Check if it's a model not found / unsupported error
      const isModelError = (d) => d?.error && (
        d.error.message?.includes('not found') || 
        d.error.message?.includes('not supported') || 
        d.error.status === 'NOT_FOUND' || 
        res.status === 404
      );

      if (isModelError(data)) {
        console.log('Model gemini-1.5-flash not found or not supported on v1. Attempting auto-discovery...');
        
        let discoveredModel = null;
        let discoveredVersion = 'v1';

        // Try listing models on v1 first
        try {
          const listRes = await fetch(`https://generativelanguage.googleapis.com/v1/models?key=${apiKey}`);
          const listData = await listRes.json();
          if (listData.models && listData.models.length > 0) {
            const candidate = listData.models.find(m => 
              m.supportedGenerationMethods?.includes('generateContent') && 
              (m.name?.includes('flash') || m.name?.includes('pro') || m.name?.includes('gemini'))
            );
            if (candidate) {
              discoveredModel = candidate.name.replace('models/', '');
              discoveredVersion = 'v1';
            }
          }
        } catch (e) {
          console.warn('Failed to list models on v1:', e);
        }

        // If not found, try listing on v1beta
        if (!discoveredModel) {
          try {
            const listRes = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
            const listData = await listRes.json();
            if (listData.models && listData.models.length > 0) {
              const candidate = listData.models.find(m => 
                m.supportedGenerationMethods?.includes('generateContent') && 
                (m.name?.includes('flash') || m.name?.includes('pro') || m.name?.includes('gemini'))
              );
              if (candidate) {
                discoveredModel = candidate.name.replace('models/', '');
                discoveredVersion = 'v1beta';
              }
            }
          } catch (e) {
            console.warn('Failed to list models on v1beta:', e);
          }
        }

        if (discoveredModel) {
          console.log(`Discovered available model: ${discoveredModel} (${discoveredVersion}). Retrying request...`);
          res = await generateWithModel(discoveredVersion, discoveredModel);
          data = res.data;
        }
      }

      if (data.error) {
        alert(`Gemini API Error: ${data.error.message || JSON.stringify(data.error)}`);
      } else {
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
        if (text) {
          setCategory(text);
        } else {
          alert('Gagal mendapatkan cadangan kategori dari AI (Tiada respon dari model).');
        }
      }
    } catch (e) {
      console.error(e);
      alert('Ralat semasa berhubung dengan Gemini API.');
    } finally {
      setIsAiLoading(false);
    }
  };

  const save = async () => {
    await onSave({
      title: title.trim() || 'Untitled Mind Map',
      description: description.trim(),
      status,
      date,
      category,
      likes: initialMap?.likes || 0,
      nodes: nodes.map(n => ({ id: n.id, type: 'mindMap', position: n.position, data: n.data })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, type: e.type })),
    }, initialMap?.id);
  };

  const edgesWithHandles = useMemo(() => {
    return edges.map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      const handles = getEdgeHandles(sourceNode, targetNode);
      return {
        ...edge,
        ...handles,
      };
    });
  }, [edges, nodes]);

  const onNodeDragStart = useCallback(() => {
    recordState(nodes, edges);
  }, [nodes, edges, recordState]);

  const selectedNode = nodes.find(n => n.selected);

  return (
    <div className="fixed inset-0 z-[100] flex animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-6xl mx-auto my-4 md:my-8 flex flex-col border shadow-2xl ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
        <div className={`flex items-center justify-between px-5 py-3 border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mind Map Title..." className={`text-lg font-serif bg-transparent outline-none flex-1 min-w-0 ${theme.text}`} />
            
            <div style={pillStyle} className={`flex items-center gap-1.5 px-3 py-1 border flex-shrink-0 ${isDarkMode ? 'bg-stone-800 border-stone-700 text-stone-400' : 'bg-stone-50 border-stone-200 text-stone-500'}`}>
              <Calendar size={11} />
              <input 
                type="date" 
                value={date} 
                onChange={(e) => setDate(e.target.value)}
                className="bg-transparent outline-none text-[9px] font-bold uppercase tracking-widest cursor-pointer w-24"
              />
            </div>

            <button
              type="button"
              onClick={handleAiSuggestCategory}
              disabled={isAiLoading}
              style={pillStyle}
              title="Auto-suggest category using Gemini AI"
              className={`p-1.5 border flex items-center justify-center transition-all flex-shrink-0 ${
                isAiLoading 
                  ? 'animate-pulse' 
                  : (isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-850 hover:text-white' : 'border-stone-200 text-stone-500 hover:bg-stone-50 hover:text-stone-800')
              }`}
            >
              {isAiLoading ? (
                <Loader2 size={12} className="animate-spin text-amber-500" />
              ) : (
                <Sparkles size={12} className="text-amber-500" />
              )}
              {category && !isAiLoading && (
                <span className="ml-1 text-[9px] font-bold uppercase tracking-widest">{category}</span>
              )}
            </button>

            <div className="flex items-center gap-0.5 border rounded-full overflow-hidden flex-shrink-0" style={pillStyle}>
              <button onClick={() => setStatus('draft')} className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest transition-all ${status === 'draft' ? (isDarkMode ? 'bg-stone-700 text-white' : 'bg-stone-900 text-white') : (isDarkMode ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600')}`}>Draft</button>
              <button onClick={() => setStatus('published')} className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest transition-all ${status === 'published' ? (isDarkMode ? 'bg-stone-700 text-white' : 'bg-stone-900 text-white') : (isDarkMode ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600')}`}>Published</button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 ml-3">
            <button onClick={addChildNode} style={pillStyle} className={`p-2 border ${isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`} title="Add Child (Tab)"><Plus size={15} /></button>
            <button onClick={handleRadialLayout} style={pillStyle} className={`p-2 border ${isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`} title="Scatter"><GitBranch size={15} /></button>
            <button onClick={deleteSelected} style={pillStyle} className={`p-2 border ${isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`} title="Delete (Del)"><Trash2 size={15} /></button>
            <div className={`w-px h-5 mx-1 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />
            <button onClick={handleUndo} disabled={!canUndo} style={pillStyle} className={`p-2 border ${!canUndo ? 'opacity-30 cursor-not-allowed' : ''} ${isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`} title="Undo (Ctrl+Z)"><Undo size={15} /></button>
            <button onClick={handleRedo} disabled={!canRedo} style={pillStyle} className={`p-2 border ${!canRedo ? 'opacity-30 cursor-not-allowed' : ''} ${isDarkMode ? 'border-stone-700 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`} title="Redo (Ctrl+Shift+Z)"><Redo size={15} /></button>
            <div className={`w-px h-5 mx-1 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />
            <button onClick={save} disabled={saveStatus === 'saving'} style={pillStyle} className={`px-4 py-2 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5 hover:opacity-80 ${saveStatus === 'saving' ? 'opacity-50 cursor-wait' : ''}`}>
              {saveStatus === 'saving' ? <Loader2 size={12} className="animate-spin" /> : null} Save
            </button>
            <button onClick={onClose} style={pillStyle} className={`p-2 ${theme.text} hover:opacity-60`}><X size={18} /></button>
          </div>
        </div>

        <div className={`px-5 py-2 border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description..." className={`w-full text-[11px] bg-transparent outline-none opacity-50 text-center ${theme.text}`} />
        </div>

        <div className="flex-1 min-h-[55vh]">
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edgesWithHandles}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDragStart={onNodeDragStart}
              nodeTypes={nodeTypes}
              defaultViewport={initialViewport}
              onInit={(instance) => {
                rfInstance.current = instance;
                setTimeout(() => {
                  instance.fitView({ padding: 0.2 });
                }, 400);
              }}
              fitView
              attributionPosition="bottom-left"
              deleteKeyCode={null}
              multiSelectionKeyCode="Shift"
              defaultEdgeOptions={{ type: 'simplebezier', style: { stroke: '#a8a29e', strokeWidth: 1.5 } }}
            >
              <Controls className={isDarkMode ? 'bg-stone-800 border-stone-700' : ''} />
              <Background color={isDarkMode ? '#292524' : '#e7e5e4'} gap={20} />
              <MiniMap nodeStrokeColor="#a8a29e" nodeColor={isDarkMode ? '#44403c' : '#d6d3d1'} maskColor={isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.08)'} style={{ border: `1px solid ${isDarkMode ? '#44403c' : '#e7e5e4'}` }} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        {selectedNode && (
          <div className={`px-5 py-3 border-t flex flex-col sm:flex-row items-start sm:items-center gap-3 ${isDarkMode ? 'border-stone-800 bg-stone-950' : 'border-stone-100 bg-stone-50'}`}>
            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest opacity-40 flex-shrink-0"><Type size={12} /> Node</div>
            <input type="text" value={selectedNode.data.label} onChange={(e) => updateWorkspaceState((nds) => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, label: e.target.value } } : n), null)} placeholder="Label..." className={`flex-1 px-3 py-1.5 text-xs border outline-none min-w-0 text-center ${isDarkMode ? 'bg-stone-800 border-stone-700 text-white' : 'bg-white border-stone-200 text-stone-900'}`} />
            <input type="text" value={selectedNode.data.description || ''} onChange={(e) => updateWorkspaceState((nds) => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, description: e.target.value } } : n), null)} placeholder="Description..." className={`flex-[2] px-3 py-1.5 text-xs border outline-none min-w-0 text-center ${isDarkMode ? 'bg-stone-800 border-stone-700 text-white' : 'bg-white border-stone-200 text-stone-900'}`} />
            <textarea 
              value={selectedNode.data.details || ''} 
              onChange={(e) => updateWorkspaceState((nds) => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, details: e.target.value } } : n), null)} 
              placeholder="Node details/notes..." 
              rows={1}
              className={`flex-[2] px-3 py-1 text-xs border outline-none min-w-0 resize-y text-center ${isDarkMode ? 'bg-stone-800 border-stone-700 text-white' : 'bg-white border-stone-200 text-stone-900'}`} 
            />
            <div className="relative flex-shrink-0">
              <button onClick={() => setShowColorPicker(showColorPicker === selectedNode.id ? null : selectedNode.id)} style={pillStyle} className={`p-1.5 border ${isDarkMode ? 'border-stone-700 text-stone-400' : 'border-stone-200 text-stone-500'}`}><Palette size={14} /></button>
              {showColorPicker === selectedNode.id && (
                <div className={`absolute bottom-full right-0 mb-2 p-2 rounded-xl border shadow-xl flex gap-1.5 ${isDarkMode ? 'bg-stone-800 border-stone-700' : 'bg-white border-stone-100'}`}>
                  {NODE_COLORS.map((color, i) => (
                    <button key={i} onClick={() => colorSelected(color)} style={{ backgroundColor: color || (isDarkMode ? '#44403c' : '#e7e5e4'), border: selectedNode.data.color === color ? '2px solid #78716c' : '2px solid transparent' }} className="w-6 h-6 rounded-full hover:scale-110 transition-transform" />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MindMapEditor;
